import React, { useMemo, useRef } from "react";
import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import DialogComponent from "../../base/Dialog.component";
import MFAOption from "./document/MFAOption.component";
import SecurityCode from "./document/SecurityCode.component";
import InsurancePullComponent from "./document/InsurancePull.component";
import {
  SELECT_MFA_OPTIONS,
  SUBMIT_MFA_OPTIONS,
  RESEND_MFA_OPTIONS,
} from "../../../graphql/mutation/mutation";
import { closeWindow, TYPE } from "../../../utils/Utils";
import { useVerification } from "../../../context/Verification.context";
import useDebounce from "../../../hook/useDebounce";
import InformationDialog from "../../base/dialog-body/Information.dialog";

const DIALOG_INITIAL_STATE = {
  open: false,
  bodyType: TYPE.DEFAULT,
  title: "",
  body: null,
};

const DocumentPull = ({
  onInsurancePull,
  isPullComplete,
  setISPullComplete,
  status,
  dialogOpen,
  stopPolling,
  startPolling,
}) => {
  const navigate = useNavigate();
  const verification = useVerification();
  const [errorNavigation, setErrorNavigation] = useState(null);
  const [dialog, setDialog] = useState(DIALOG_INITIAL_STATE);
  const [selectedOption, setSelectedOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [securityCode, setSecurityCode] = useState("");
  const [authStatus, setAuthStatus] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [isResendResponse, setIsResendResponse] = useState(false);
  const [mfaOptionConfirmed, setMfaOptionConfirmed] = useState(false);
  const [mfaSubmitted, setMfaSubmitted] = useState(false);
  const [uiPhase, setUiPhase] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);
  const [resendOptionKey, setResendOptionKey] = useState(null); // Store option_key from resend
  const [statusStuckTimer, setStatusStuckTimer] = useState(null); // Track if status is stuck

  const latestPullRef = useRef({
    pullId: verification.pullInsurance?.pullId,
    pullJwt: verification.pullInsurance?.pullJwt,
  });

  const autoTriggerTimerRef = useRef(null); // Ref to track auto-trigger timer

  const [submitMfa, { loading: submitLoading, data: submittedMFAResponse }] =
    useMutation(SUBMIT_MFA_OPTIONS);
  const [selectMfa, { loading: selectLoading, data: selectedMFAResponse }] =
    useMutation(SELECT_MFA_OPTIONS);
  const [resendMfa, { loading: resendLoading, data: resendMFAResponse }] =
    useMutation(RESEND_MFA_OPTIONS);

  // Handle resend response
  useEffect(() => {
    if (resendMFAResponse && isResendResponse) {
      console.log("RESEND RESPONSE:", resendMFAResponse);

      // IMPORTANT:
      // When the GraphQL layer returns { data: { resendMfaOption: null } },
      // we want to treat that as a null/empty backend response and NOT as
      // a structured object missing pull_id/pull_jwt (which would trigger
      // the "Invalid response from server" dialog).
      //
      // So:
      // - If the key exists and is explicitly null, treat as null.
      // - If the key is undefined (different shape), fall back to the whole object.
      const hasField = Object.prototype.hasOwnProperty.call(
        resendMFAResponse,
        "resendMfaOption",
      );
      const response = hasField
        ? resendMFAResponse.resendMfaOption
        : resendMFAResponse;

      // Handle null/empty response gracefully:
      // - Do NOT block the user with an error dialog
      // - Let existing polling continue so the user can still enter the code
      // This situation typically means the GraphQL layer returned null while the backend
      // still processed the resend (e.g., email/SMS actually sent).
      if (!response) {
        console.warn(
          "Resend returned null/empty response. Keeping existing pull and allowing user to continue.",
        );

        setIsResending(false);
        setIsResendResponse(false);
        verification.setIsResending(false);

        // Ensure polling is active for whatever pull we currently have
        setTimeout(() => {
          const currentPullId =
            latestPullRef.current.pullId || verification?.pullInsurance?.pullId;
          if (startPolling && currentPullId) {
            console.log(
              "Ensuring polling continues for existing pull after null resend response:",
              currentPullId,
            );
            startPolling(2000);
          }
        }, 500);
        return;
      }

      // PRIORITY: If we have pull_id and pull_jwt, treat as success regardless of errors
      // This handles cases where selectMfaOption failed but pull was created and code was sent
      if (response.pull_id && response.pull_jwt) {
        console.log("Resend successful - new pull created:", response.pull_id);
        if (response.errors && response.errors.length > 0) {
          console.log(
            "Note: Some errors occurred but pull is valid. Backend will verify code was sent.",
            response.errors,
          );
        }

        latestPullRef.current = {
          pullId: response.pull_id,
          pullJwt: response.pull_jwt,
        };

        verification.setPullInsurance({
          ...verification.pullInsurance,
          pullId: response.pull_id,
          pullJwt: response.pull_jwt,
        });

        setMfaOptionConfirmed(true);
        setSecurityCode("");
        setAutoSelectAttempted(false); // Reset for new pull
        // Keep resendOptionKey - we'll need it for auto-trigger if backend's selectMfaOption failed

        // Stop polling for old pull first
        if (stopPolling) {
          stopPolling();
        }

        // Set a timer to auto-trigger selectMfaOption if status stays at IDENTITY_VERIFICATION_OPTIONS too long
        // This handles cases where backend's selectMfaOption failed or timed out
        if (statusStuckTimer) {
          clearTimeout(statusStuckTimer);
        }

        const stuckTimer = setTimeout(() => {
          console.warn(
            "Status has been at IDENTITY_VERIFICATION_OPTIONS for 20 seconds. Backend's selectMfaOption may have failed.",
          );
          console.warn(
            "Will attempt frontend auto-trigger if status is still IDENTITY_VERIFICATION_OPTIONS...",
          );
          // The auto-trigger logic in useEffect will handle this
        }, 20000); // 20 seconds

        setStatusStuckTimer(stuckTimer);

        // Start polling for the new pull after a brief delay
        // This ensures the query updates with the new pull_id
        setTimeout(() => {
          if (startPolling) {
            console.log("Starting polling for new pull:", response.pull_id);
            startPolling(2000);
          }
        }, 500);

        // Don't show dialog - just continue with the new pull
        // User can enter the code that was sent
        // Status will be updated via polling
        // Frontend will auto-call selectMfaOption when it detects IDENTITY_VERIFICATION_OPTIONS
        setIsResending(false);
        setIsResendResponse(false);
        verification.setIsResending(false);
        return;
      }

      // Only show errors if we don't have valid pull_id and pull_jwt
      if (response.errors && response.errors.length > 0) {
        console.error(
          "Resend returned errors and no valid pull:",
          response.errors,
        );
        const errorMessage =
          response.errors[0]?.message ||
          "Failed to resend code. Please try again";

        // No pull_id/pull_jwt - show error and restart old pull polling
        setDialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "Resend failed",
          body: <InformationDialog text={errorMessage} />,
        });

        // Restart polling for the old pull in case of error
        // Use a small delay to ensure stopPolling has completed
        setTimeout(() => {
          if (startPolling && verification?.pullInsurance?.pullId) {
            console.log(
              "Restarting polling for old pull after resend error (no new pull)",
            );
            startPolling(2000);
          }
        }, 500);

        setIsResending(false);
        setIsResendResponse(false);
        verification.setIsResending(false);
        return;
      }

      // Check if we have the required pull_id and pull_jwt
      if (!response.pull_id || !response.pull_jwt) {
        console.error("Resend response missing pull_id or pull_jwt:", response);
        setDialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "Resend failed",
          body: (
            <InformationDialog text="Failed to resend code. Invalid response from server." />
          ),
        });
        setIsResending(false);
        setIsResendResponse(false);
        verification.setIsResending(false);

        // Restart polling for the old pull in case of error
        // Use a small delay to ensure stopPolling has completed
        setTimeout(() => {
          if (startPolling && verification?.pullInsurance?.pullId) {
            console.log(
              "Restarting polling for old pull after resend error (missing pull_id/jwt)",
            );
            startPolling(2000);
          }
        }, 500);
        return;
      }

      // Success - update with new pull information
      console.log(
        "Resend successful, updating pull_id:",
        response.pull_id,
        "pull_jwt:",
        response.pull_jwt ? "***" : "missing",
      );

      // Update the latest pull reference
      latestPullRef.current = {
        pullId: response.pull_id,
        pullJwt: response.pull_jwt,
      };

      // Stop polling for the old pull
      if (stopPolling) {
        stopPolling();
      }

      // Update verification context with new pull info
      verification.setPullInsurance({
        ...verification.pullInsurance,
        pullId: response.pull_id,
        pullJwt: response.pull_jwt,
      });

      // Mark MFA option as confirmed
      setMfaOptionConfirmed(true);

      // Clear security code input
      setSecurityCode("");

      // Start polling for the new pull
      if (startPolling) {
        startPolling(2000);
      }

      setIsResending(false);
      setIsResendResponse(false);
      verification.setIsResending(false);
    }
  }, [
    resendMFAResponse,
    stopPolling,
    startPolling,
    verification,
    isResendResponse,
  ]);

  useEffect(() => {
    if (selectedMFAResponse) {
      console.log("SELECT MFA RESPONSE:", selectedMFAResponse);
      const response =
        selectedMFAResponse.selectMfaOption || selectedMFAResponse;

      if (response?.errors && response.errors.length > 0) {
        console.error("selectMfaOption returned errors:", response.errors);
        // Don't show error dialog - let it retry or user can manually resend
        // The status will remain at IDENTITY_VERIFICATION_OPTIONS and auto-trigger will retry
      } else if (response?.pull_id) {
        console.log("MFA option confirmed with pull_id:", response.pull_id);
        setMfaOptionConfirmed(true);
        // Update latest pull ref if we got new credentials
        if (response.pull_jwt) {
          latestPullRef.current = {
            pullId: response.pull_id,
            pullJwt: response.pull_jwt,
          };
        }
      }
    }
  }, [selectedMFAResponse]);

  useEffect(() => {
    if (submittedMFAResponse) {
      console.log("RAW RESPONSE:", JSON.stringify(submittedMFAResponse));
      const response =
        submittedMFAResponse.submitMfaOption || submittedMFAResponse;
      console.log("PARSED RESPONSE:", JSON.stringify(response));

      // Only handle hard errors from backend — let polling handle success/wrong code
      if (response?.errors && response.errors.length > 0) {
        console.log("Submit failed with errors:", response.errors);
        // Always show the product-approved copy for MFA failures.
        // Backend may return generic transport errors like "Bad Request".
        const errorMessage = "Please double check your MFA code and try again.";
        setSubmitError(true);
        setMfaSubmitted(false);
        // Return to the code-entry screen before showing the error dialog
        setUiPhase(false);
        setDialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "That MFA code did not work",
          body: <InformationDialog text={errorMessage} />,
        });
        setSecurityCode("");
      } else {
        console.log(
          "MFA submitted successfully — waiting for polling to progress status",
        );
        setMfaSubmitted(true);
        if (startPolling) {
          startPolling(2000); // ← keep polling so insurance pull continues
        }
      }
    }
  }, [submittedMFAResponse]);

  useEffect(() => {
    if (onInsurancePull && status) {
      console.log("onInsurancePull update:", { status, data: onInsurancePull });
      let pullStatus = onInsurancePull.status;
      let pullData = JSON.parse(onInsurancePull.data);
      console.log("Parsed pullData:", pullData);

      if (pullData?.auth_status) {
        console.log("Setting authStatus to:", pullData.auth_status);
        setAuthStatus(pullData.auth_status);
      } else {
        // Don't wipe authStatus when the backend omits it for a tick.
        // Canopy often stops including auth_status once it progresses, and clearing here
        // would incorrectly keep the user stuck on the SecurityCode screen.
        console.log(
          "auth_status missing for this tick; keeping previous authStatus",
        );
      }

      // AUTO-TRIGGER LOGIC: If we're in resend flow and status is IDENTITY_VERIFICATION_OPTIONS,
      // automatically call selectMfaOption if backend's attempt failed or timed out
      // This is a critical fallback to ensure code is sent
      if (
        mfaOptionConfirmed &&
        !autoSelectAttempted &&
        (pullData?.auth_status === "IDENTITY_VERIFICATION_OPTIONS" ||
          pullStatus === "IDENTITY_VERIFICATION_OPTIONS") &&
        status === "IDENTITY_VERIFICATION_OPTIONS"
      ) {
        // Use resendOptionKey if available, otherwise use selectedOption
        // Normalize the key to ensure consistency
        const rawOptionKey = resendOptionKey || selectedOption?.key;
        const optionKeyToUse = normalizeOptionKey(rawOptionKey);
        const currentPullId =
          latestPullRef.current.pullId || verification.pullInsurance?.pullId;
        const currentPullJwt =
          latestPullRef.current.pullJwt || verification.pullInsurance?.pullJwt;

        if (currentPullId && currentPullJwt && optionKeyToUse) {
          // Clear any existing timer
          if (autoTriggerTimerRef.current) {
            clearTimeout(autoTriggerTimerRef.current);
            autoTriggerTimerRef.current = null;
          }

          // Wait a bit to see if backend's selectMfaOption worked
          // Backend has up to ~45s for IDENTITY_VERIFICATION_OPTIONS + ~24s for verification = ~69s total
          // But we'll trigger earlier (8 seconds) to be more responsive
          const autoTriggerDelay = 8000; // 8 seconds - gives backend time but not too long

          console.log(
            `Setting auto-trigger timer for ${autoTriggerDelay}ms. If status is still IDENTITY_VERIFICATION_OPTIONS, will trigger selectMfaOption.`,
          );

          autoTriggerTimerRef.current = setTimeout(() => {
            // Double-check status and that we haven't already attempted
            if (
              !autoSelectAttempted &&
              status === "IDENTITY_VERIFICATION_OPTIONS"
            ) {
              console.log(
                "Auto-trigger: Status still IDENTITY_VERIFICATION_OPTIONS after delay. Backend's selectMfaOption may have failed.",
              );
              console.log(
                "Auto-triggering selectMfaOption from frontend as fallback...",
              );
              console.log(
                "Using option_key:",
                optionKeyToUse,
                "for pull:",
                currentPullId,
              );

              setAutoSelectAttempted(true); // Prevent duplicate calls

              selectMfa({
                variables: {
                  mfaInput: {
                    pull_id: currentPullId,
                    pull_jwt: currentPullJwt,
                    option_key: optionKeyToUse,
                    user_id: parseInt(verification?.customer?.userId),
                    employee_user_id: parseInt(verification?.employeeId),
                    dealership_id: parseInt(verification?.dealershipId),
                  },
                },
              }).catch((error) => {
                console.error("Auto-trigger selectMfaOption failed:", error);
                // Reset so it can retry after a delay
                setTimeout(() => {
                  setAutoSelectAttempted(false);
                }, 5000);
              });
            } else {
              console.log(
                "Auto-trigger skipped - selectMfaOption already attempted or status changed",
              );
            }
            autoTriggerTimerRef.current = null;
          }, autoTriggerDelay);
        } else {
          console.warn("Cannot auto-trigger selectMfaOption - missing:", {
            hasPullId: !!currentPullId,
            hasPullJwt: !!currentPullJwt,
            hasOptionKey: !!optionKeyToUse,
            resendOptionKey: resendOptionKey,
            selectedOptionKey: selectedOption?.key,
          });
        }
      }

      // Reset autoSelectAttempted and clear timer when status changes to IDENTITY_VERIFICATION
      if (status === "IDENTITY_VERIFICATION") {
        if (autoSelectAttempted) {
          console.log(
            "Status changed to IDENTITY_VERIFICATION - selectMfaOption succeeded!",
          );
          setAutoSelectAttempted(false);
        }
        // Clear all timers
        if (autoTriggerTimerRef.current) {
          clearTimeout(autoTriggerTimerRef.current);
          autoTriggerTimerRef.current = null;
        }
        if (statusStuckTimer) {
          clearTimeout(statusStuckTimer);
          setStatusStuckTimer(null);
        }
        // Clear resendOptionKey once we're past IDENTITY_VERIFICATION_OPTIONS
        if (resendOptionKey) {
          setResendOptionKey(null);
        }
      }

      if (!mfaOptionConfirmed) {
        if (
          pullData?.auth_status === "IDENTITY_VERIFICATION_OPTIONS" ||
          pullStatus === "IDENTITY_VERIFICATION_OPTIONS"
        ) {
          console.log("Setting MFA options:", pullData.mfa_options);
          const mfaOptions = mfaOptionsHandler(pullData.mfa_options);
          setOptions(mfaOptions);

          // If we have a resendOptionKey from a previous resend, restore the selectedOption
          if (resendOptionKey && !selectedOption) {
            const matchingOption = mfaOptions.find(
              (opt) => opt.key === resendOptionKey,
            );
            if (matchingOption) {
              console.log(
                "Restoring selectedOption from resendOptionKey:",
                resendOptionKey,
                matchingOption,
              );
              setSelectedOption(matchingOption);
            }
          }
        }
      } else if (mfaOptionConfirmed && resendOptionKey && !selectedOption) {
        // In resend flow, if we have resendOptionKey but no selectedOption, try to restore it
        if (pullData?.mfa_options) {
          const mfaOptions = mfaOptionsHandler(pullData.mfa_options);
          const matchingOption = mfaOptions.find(
            (opt) => opt.key === resendOptionKey,
          );
          if (matchingOption) {
            console.log(
              "Restoring selectedOption in resend flow:",
              resendOptionKey,
              matchingOption,
            );
            setSelectedOption(matchingOption);
          }
        }
      }

      if (
        pullData?.auth_status === "IDENTITY_VERIFICATION" ||
        pullStatus === "IDENTITY_VERIFICATION"
      ) {
        console.log("Setting selected option from pullData:", {
          mfa_input_type: pullData.mfa_input_type,
          mfa_input_display: pullData.mfa_input_display,
        });

        // Wrong code — show error and reset
        if (pullData?.login_error_message) {
          setSubmitError(true);
          setMfaSubmitted(false);
          setUiPhase(false);
          setDialog({
            open: true,
            bodyType: TYPE.DEFAULT,
            title: "That MFA code did not work",
            body: (
              <InformationDialog text="Please double check your MFA code and try again." />
            ),
          });
          setSecurityCode("");
        }

        if (pullData.mfa_input_type === "SECURITY_QUESTION") {
          setSelectedOption({
            key: pullData.mfa_input_type,
            value: pullData.mfa_input_display,
            message: pullData.mfa_input_display,
          });
        } 
      }
    }
  }, [onInsurancePull, status, mfaOptionConfirmed, mfaSubmitted]);

  // Helper function to normalize MFA option keys for consistency
  const normalizeOptionKey = (key) => {
    if (!key) return key;
    const lowerKey = key.toLowerCase();

    // if (lowerKey.startsWith("email")) {
    //   return "email";
    // } else if (lowerKey.startsWith("sms")) {
    //   return "sms";
    // } else if (lowerKey.startsWith("voice")) {
    //   return "voice";
    // } else if (lowerKey.startsWith("pin")) {
    //   return "pin";
    // } else if (lowerKey.startsWith("security")) {
    //   return "securityQuestion";
    // } else if (lowerKey.startsWith("token")) {
    //   return "token";
    // }
    return key; // Return original if no match
  };

  const mfaOptionsHandler = (options) => {
    let arr = [];
    for (let [key, value] of Object.entries(options)) {
      let message = "Unknown input. Please contact DeliveryIQ+";
      let normalizedKey = key; // Default to original key

      if (key !== "__typename") {
        // Normalize keys to ensure consistency (e.g., "EMAIL_OTP" -> "email", "SMS_OTP" -> "sms")
        const lowerKey = key.toLowerCase();

        if (lowerKey.startsWith("email")) {
          // normalizedKey = "email";
          value = `${value}`;
          message = "You should receive the code to your email";
        } else if (lowerKey.startsWith("sms")) {
          // Handle sms0, sms1, SMS_OTP, etc. -> normalize to "sms"
          // normalizedKey = "sms";
          value = `${value}`;
          message = "You should receive text to mobile number";
        } else if (lowerKey.startsWith("voice")) {
          // normalizedKey = "voice";
          value = `${value}`;
          message = "You should receive call to mobile number";
        } else if (lowerKey.startsWith("pin")) {
          // normalizedKey = "pin";
          value = `${value}`;
          message = "Please enter your pin";
        } else if (lowerKey.startsWith("security")) {
          // normalizedKey = "securityQuestion";
          value = `${value}`;
          message = "Please enter your answer";
        } else if (lowerKey.startsWith("token")) {
          // normalizedKey = "token";
          message = "Enter your valid code";
          value = `${value}`;
        }

        arr.push({ key: normalizedKey, value, message });
      }
    }
    return arr;
  };

  const mfaSelectedHandler = (event) => {
    console.log("Selecting MFA option:", selectedOption);
    const normalizedKey = normalizeOptionKey(selectedOption?.key);
    selectMfa({
      variables: {
        mfaInput: {
          pull_id: verification.pullInsurance.pullId,
          pull_jwt: verification.pullInsurance.pullJwt,
          option_key: normalizedKey,
          user_id: parseInt(verification?.customer?.userId),
          employee_user_id: parseInt(verification?.employeeId),
          dealership_id: parseInt(verification?.dealershipId),
        },
      },
    });
  };

  const handleResendCode = async () => {
    if (!selectedOption?.key) {
      console.error("No option selected for resend");
      setDialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Resend failed",
        body: (
          <InformationDialog text="No MFA option selected. Please select an option first." />
        ),
      });
      return;
    }

    if (isResending) {
      console.log("Resend already in progress");
      return;
    }

    const normalizedKey = normalizeOptionKey(selectedOption.key);
    console.log(
      "Resending code for option:",
      selectedOption.key,
      "-> normalized to:",
      normalizedKey,
    );

    // Store the normalized option_key for auto-trigger fallback
    setResendOptionKey(normalizedKey);
    setAutoSelectAttempted(false); // Reset for new resend

    setIsResending(true);
    setIsResendResponse(true);
    verification.setIsResending(true);

    // Stop polling for the old pull BEFORE making the request
    // This prevents the old pull from continuing to poll while resend is in progress
    if (stopPolling) {
      console.log("Stopping polling for old pull before resend");
      stopPolling();
    }

    try {
      await resendMfa({
        variables: {
          resendMfaInput: {
            insuranceInput: {
              employee_user_id: parseInt(verification?.employeeId),
              device_identifier: "davi",
              terms_version: 1,
              consent_language:
                "By clicking continue you agree to the Canopy Connect End User Privacy Policy and Terms of Service",
              public_alias: process.env.REACT_APP_CANOPY_PUBLIC_ALIAS,
              insurer_name: verification?.pullInsurance?.carrier,
              insurer_username: verification?.pullInsurance?.username,
              insurer_password: verification?.pullInsurance?.password,
              insurer_thirdField: verification?.pullInsurance?.thirdField,
              // DO NOT include old pull_id or pull_jwt - backend will create a new pull
              customer: {
                user_id: parseInt(verification?.customer?.userId),
                first_name: verification?.customer?.firstName,
                last_name: verification?.customer?.lastName,
                email: verification?.customer?.email,
                mobile_number: verification?.customer?.phoneNumber,
                vin: verification?.customer?.vin,
              },
              transaction_id: verification?.pullInsurance?.transaction_id,
              dealership_id: parseInt(verification?.dealershipId),
            },
            mfaInput: {
              option_key: normalizedKey,
              user_id: parseInt(verification?.customer?.userId),
              employee_user_id: parseInt(verification?.employeeId),
              dealership_id: parseInt(verification?.dealershipId),
            },
          },
        },
      });
    } catch (error) {
      console.error("Resend mutation error:", error);
      setIsResending(false);
      setIsResendResponse(false);
      verification.setIsResending(false);

      // Show error dialog
      const errorMessage =
        error?.message ||
        error?.graphQLErrors?.[0]?.message ||
        "Failed to resend code. Please try again.";
      setDialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Resend failed",
        body: <InformationDialog text={errorMessage} />,
      });

      // Restart polling for the old pull in case of error
      // Use a small delay to ensure stopPolling has completed
      setTimeout(() => {
        if (startPolling && verification?.pullInsurance?.pullId) {
          console.log("Restarting polling for old pull after mutation error");
          startPolling(2000);
        }
      }, 500);
    }
  };

  const mfaSubmitHandler = () => {
    const currentPullId =
      latestPullRef.current.pullId || verification.pullInsurance?.pullId;
    const currentPullJwt =
      latestPullRef.current.pullJwt || verification.pullInsurance?.pullJwt;

    console.log("Submitting MFA code with latest pull:", {
      pull_id: currentPullId,
      pull_jwt: currentPullJwt,
      mfa_value: securityCode,
    });

    if (!currentPullId) {
      console.error("No pull ID available for submission");
      return;
    }

    if (!currentPullJwt) {
      console.error("No pull JWT available for submission");
      return;
    }

    // Immediately navigate to the insurance-pulling loading screen.
    // If the code is wrong, the submittedMFAResponse handler will reset uiPhase
    // and show the error dialog.
    setMfaSubmitted(true);
    setUiPhase(true);

    // Keep polling so the backend can validate the code and progress the pull.
    submitMfa({
      variables: {
        mfaInput: {
          pull_id: currentPullId,
          pull_jwt: currentPullJwt,
          mfa_value: securityCode,
        },
      },
    });
  };

  const renderComponent = useMemo(
    () => () => {
      if (dialogOpen) {
        return null;
      }

      if (uiPhase) {
        return (
          <InsurancePullComponent
            status={status}
            isPullComplete={isPullComplete}
            setISPullComplete={setISPullComplete}
            authStatus={authStatus}
            uiPhase={uiPhase}
          />
        );
      }

      if (mfaOptionConfirmed && status === "IDENTITY_VERIFICATION_OPTIONS") {
        console.log(
          "Rendering SecurityCode (confirmed, but status is OPTIONS)",
        );
        return (
          <SecurityCode
            selectedOption={selectedOption}
            securityCode={securityCode}
            setSecurityCode={setSecurityCode}
            mfaSubmitHandler={mfaSubmitHandler}
            status={status}
            mfaSelectedHandler={mfaSelectedHandler}
            onResendCode={handleResendCode}
            isResending={isResending || resendLoading}
            submitError={submitError}
            onSubmitErrorHandled={() => setSubmitError(false)}
          />
        );
      }

      // After Submit, stay on SecurityCode until Canopy actually leaves IDV (auth_status changes).
      // However, Canopy may stop including auth_status once it progresses; in that case, use the outer
      // status as a fallback signal to move forward.
      // if (mfaSubmitted) {
      //   const progressedStatuses = new Set([
      //     "PROCESSING",
      //     "PULLING_DATA",
      //     "GETTING_CONSUMERS",
      //     "SUCCESS",
      //     "COMPLETE",
      //   ]);

      //   const hasProgressed = progressedStatuses.has(status);

      //   if (authStatus === "IDENTITY_VERIFICATION" && !hasProgressed) {
      //     console.log(
      //       "MFA submitted - waiting for auth_status to change (stay on SecurityCode)",
      //     );
      //     return (
      //       <SecurityCode
      //         selectedOption={selectedOption}
      //         securityCode={securityCode}
      //         setSecurityCode={setSecurityCode}
      //         mfaSubmitHandler={mfaSubmitHandler}
      //         status={status}
      //         mfaSelectedHandler={mfaSelectedHandler}
      //         onResendCode={handleResendCode}
      //         isResending={isResending || resendLoading}
      //         submitError={submitError}
      //         onSubmitErrorHandled={() => setSubmitError(false)}
      //       />
      //     );
      //   }

      //   console.log("MFA submitted - progressing to InsurancePullComponent");
      //   return (
      //     <InsurancePullComponent
      //       status={status}
      //       isPullComplete={isPullComplete}
      //       setISPullComplete={setISPullComplete}
      //       authStatus={authStatus}
      //     />
      //   );
      // }

      // Keep SecurityCode "sticky" during IDV/MFA.
      // Canopy may temporarily report outer status as PROCESSING/PULLING_DATA while still in IDV,
      // and `authStatus` can momentarily be null before the next poll tick parses it again.
      // Without this, the UI can flash to the loading screen and then back to SecurityCode.
      const shouldKeepSecurityCode =
        authStatus === "IDENTITY_VERIFICATION" ||
        status === "IDENTITY_VERIFICATION" ||
        // During resend, outer status may "progress" briefly; keep SecurityCode to avoid flashing.
        ((isResending || resendLoading) && mfaOptionConfirmed) ||
        (mfaOptionConfirmed &&
          !!resendOptionKey &&
          (status === "PROCESSING" ||
            status === "PULLING_DATA" ||
            status === "GETTING_CONSUMERS")) ||
        // If we have an MFA option selected/confirmed but authStatus is temporarily missing,
        // prefer staying on SecurityCode during intermediate pull statuses.
        (mfaOptionConfirmed &&
          selectedOption &&
          !mfaSubmitted &&
          !authStatus &&
          (status === "PROCESSING" ||
            status === "PULLING_DATA" ||
            status === "GETTING_CONSUMERS"));

      if (shouldKeepSecurityCode) {
        console.log("Rendering SecurityCode");
        return (
          <SecurityCode
            selectedOption={selectedOption}
            securityCode={securityCode}
            setSecurityCode={setSecurityCode}
            mfaSubmitHandler={mfaSubmitHandler}
            status={status}
            mfaSelectedHandler={mfaSelectedHandler}
            onResendCode={handleResendCode}
            isResending={isResending || resendLoading}
            submitError={submitError}
            onSubmitErrorHandled={() => setSubmitError(false)}
          />
        );
      }

      // While RESEND flow is active (identified by resendOptionKey),
      // keep user on SecurityCode screen even if backend status temporarily
      // moves to PROCESSING or PULLING_DATA.
      // After normal (non-resend) submit, let it go to the pull-insurance loading screen.

      if (status === "IDENTITY_VERIFICATION_OPTIONS") {
        console.log("Rendering MFAOption");
        return (
          <MFAOption
            mfaSelectedHandler={mfaSelectedHandler}
            setSelectedOption={setSelectedOption}
            options={options}
            selectedOption={selectedOption}
          />
        );
      }

      if (
        status === "PROCESSING" ||
        status === "SUCCESS" ||
        status === "GETTING_CONSUMERS" ||
        status === "PULLING_DATA" ||
        authStatus
      ) {
        console.log("Rendering InsurancePullComponent with status:", status);
        return (
          <InsurancePullComponent
            status={status}
            isPullComplete={isPullComplete}
            setISPullComplete={setISPullComplete}
            authStatus={authStatus}
          />
        );
      }

      console.log("Rendering default InsurancePullComponent");
      return (
        <InsurancePullComponent
          status={status}
          isPullComplete={isPullComplete}
          setISPullComplete={setISPullComplete}
          authStatus={authStatus}
        />
      );
    },
    [
      uiPhase,
      status,
      selectedOption,
      securityCode,
      isResending,
      resendLoading,
      isPullComplete,
      dialogOpen,
      authStatus,
      options,
      mfaOptionConfirmed,
      submitError,
      resendOptionKey,
      statusStuckTimer,
    ],
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoTriggerTimerRef.current) {
        clearTimeout(autoTriggerTimerRef.current);
      }
      if (statusStuckTimer) {
        clearTimeout(statusStuckTimer);
      }
    };
  }, [statusStuckTimer]);

  useDebounce(
    () => {
      if (status === "PROCESSING") {
        setDialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "System Down",
          body: (
            <InformationDialog
              text={"System is temporarily down. Please try again later"}
            />
          ),
        });
      }
    },
    60 * 1000,
    [status],
  );

  return (
    <>
      {!dialogOpen && renderComponent()}

      <DialogComponent
        open={dialog.open}
        bodyType={dialog.bodyType}
        body={dialog.body}
        title={dialog.title}
        onClose={() => {
          setDialog({ ...dialog, open: false });
          // Only navigate or close window if errorNavigation is set
          // For regular dialogs (like resend errors), just close the dialog
          if (errorNavigation !== null) {
            navigate(errorNavigation);
          }
          // Don't call closeWindow() here - let user continue with verification
        }}
      />
    </>
  );
};

export default DocumentPull;
